import React, { useContext, useEffect } from "react";
import { isNil } from "lodash";
import { observer } from "mobx-react-lite";
import validator from "validator";
import { useEffectOnce, useForceUpdate, useInput } from "@lib-shared/hooks";
import { UserPermission } from "core/constants";
import { AppContext } from "core/context";
import { getErrorMessage, logApiError } from "core/lib";
import signalRHelper from "core/lib/signal-r-helper";
import userPermissionsManager from "core/lib/user-permissions.manager";
import { ApproveUserRequest, InviteUserRequest } from "core/models/user-permissions.request";
import PermissionService from "core/services/cef/permission.service";
import { DeleteUserRequest } from "core/services/cef/permission.service.types";
import UserModalStore, { PermissionThatCanBeGiven } from "core/stores/user-modal.store";
import { ShowModal, ModalAlert, ValidationMessage, ModalIcon, Button } from "components/common-ui";
import ModalDialog from "components/ui/modal-dialog";
import UserPermissionCheckboxes from "./permission-checkboxes";
import UserDetailsWrapper from "../user-details-wrapper";
import { UserPermissions } from "core/models/user";

type NewUserFormProps = {
  id: string,
  onUpdate: () => void,
  modalStore: UserModalStore
}

const hasValue = (value: any) => value !== undefined && value?.trim().length;

const inputOptions = (name: string, errorMessage: string) => {
  return {
    id: name.split(" ").join("").toLowerCase(),
    name,
    errorMessage
  };
}

const newUserFormOptions = {
  firstName: inputOptions("Name", "Please enter a name"),
  lastName: inputOptions("Surname", "Please enter a surname"),
  phone: inputOptions("Contact Number", "Please enter a number"),
  email: inputOptions("Email", "Please enter an email"),
  role: inputOptions("Role", "Please enter the user's role"),
};

const perm = (isEnabled = false, permission: number, name: string) => {
  return {
    isEnabled, permission, name, isChecked: false
  };
}

const getPermissionsList = (can?: UserPermissions) => {
  if (!can) {
    return [];
  }

  const permissionsList: PermissionThatCanBeGiven[] = [
    perm(can?.clearError, UserPermission.clearError, "Clear Error"),
    perm(can?.viewBalance, UserPermission.balance, "View Balance"),
    perm(can?.makePayment, UserPermission.payAccount, "Pay Account"),
    perm(can?.viewStatements, UserPermission.statements, "View Statements"),
    perm(can?.viewActivityLogs, UserPermission.viewUserActivityLogging, "View User Activity Logs")
  ];

  return permissionsList;
}

//TODO: Refactor - too long & confusing
const NewUserForm = observer((props: NewUserFormProps) => {
  const { id, onUpdate, modalStore } = props;
  const appContext = useContext(AppContext);

  const forceUpdate = useForceUpdate();

  //#region Permissions
  const can = appContext?.user?.can;

  const permissionsList = getPermissionsList(can);

  if (!userPermissionsManager.hasParent()) {
    const allUsersItem = perm(true, UserPermission.addUsers, "Add New Users");
    permissionsList.push(allUsersItem);
  }

  useEffectOnce(() => {
    modalStore.updatePermissions(permissionsList);
  });
  //#endregion

  const user = modalStore.user;
  const isUpdate = modalStore.isUpdate;

  const modalPageNumber = modalStore.pageNumber;
  const isPageOne = modalPageNumber === 1;
  const showPermissions = !isPageOne;

  let existingDialog: any;

  const isNewInvite = () => !isUpdate && !user;

  const isApproval = () => !isUpdate && user;

  useEffect(() => {
    if (!isUpdate) {
      return;
    }

    modalStore.updatePageNumber(2);
  }, [isUpdate]);

  useEffect(() => {
    if (!modalStore.display) {
      return;
    }

    const userGrantValues = user?.permissions?.map((x: any) => x.grantValue) ?? [];

    modalStore.permissions.forEach(x => modalStore.updatePermission(x.permission, userGrantValues?.includes(x.permission)));
  }, [modalStore.display]);

  //#region Form Inputs
  const options = newUserFormOptions;

  const firstNameInput = useInput(hasValue, options.firstName);
  const lastNameInput = useInput(hasValue, options.lastName);
  const roleInput = useInput(hasValue, options.role);
  const phoneInput = useInput((value: any) => hasValue(value) && validator.isMobilePhone(value), options.phone);
  const emailInput = useInput((value: any) => hasValue(value) && validator.isEmail(value), options.email);
  const allInputs = [firstNameInput, lastNameInput, roleInput, phoneInput, emailInput];

  const resetInputs = () => allInputs.forEach(x => x.reset());

  const inputs = isNewInvite()
    ? allInputs
    : isApproval()
      ? [firstNameInput, lastNameInput]
      : [];

  const areInputsAllValid = () => inputs.every(x => x.isValid);

  const forceInputValidation = () => inputs.forEach(x => x.inputBlurHandler());
  //#endregion

  const formIsValid = areInputsAllValid();

  const onClose = () => {
    resetInputs();

    forceUpdate();

    modalStore.reset();
  }

  modalStore.updateOnClose(onClose);

  const nextPage = () => {
    if (areInputsAllValid()) {
      modalStore.updatePageNumber(2);
      return;
    }

    forceInputValidation();
  }

  //#region Checkbox Handlers
  const handlePermissionChange = (isChecked: boolean, item: PermissionThatCanBeGiven) => {
    modalStore.updatePermission(item.permission, isChecked);

    forceUpdate();
  }

  const handleAllCheckboxChange = (isChecked: boolean) => {
    modalStore.updateAllPermissions(isChecked);
  }
  //#endregion

  //#region Modal Consts
  const showDeleteConfirmation = () => {
    if (!user) {
      return;
    }

    modalStore.reset();

    existingDialog = ShowModal({
      title: "Are you sure?",
      icon: ModalIcon.warning,
      body: "Do you want to delete this user?<br/>This process cannot be undone.",
      buttonText: "Delete",
      showCancel: true,
      showClose: false,
      onClose: () => deleteUser(user)
    });
  }

  const deleteUser = async (user: UserDetailsWrapper) => {
    pendingModal("Deleting this user");

    const request: DeleteUserRequest = {
      ciamId: user.ciam,
      agreementIds: user.agreementIds ?? [],
      customerNumber: user.customerNumber,
      permanently: false
    };

    try {
      await new PermissionService().deleteUser(request, user);
      signalRHelper.notifyPermissionUpdate(user.ciam);

      successModal("User has been deleted");
    }
    catch (error) {
      logApiError(error);

      errorModal(getErrorMessage(error), "Error occurred deleting user");
    }
  }

  const pendingModal = (text: string) => {
    existingDialog = ModalAlert.loading(text);
  }

  const errorModal = (text: string, title = "An error occurred!") => {
    closePendingModal();

    onClose();

    existingDialog = ModalAlert.error(text, title);
  }

  const successModal = (text: string, title = "Your request was submitted successfully.") => {
    closePendingModal();

    onClose();

    onUpdate();

    ModalAlert.success(title, text);
  }

  const closePendingModal = () => {
    if (existingDialog == null) {
      return;
    }

    ModalAlert.close(existingDialog);

    existingDialog = null;
  }
  //#endregion

  //#region User Actions
  const _inviteUser = async () => {
    pendingModal("Adding your user...");

    const request: InviteUserRequest = {
      firstName: firstNameInput.value,
      lastName: lastNameInput.value,
      email: emailInput.value,
      phone: phoneInput.value,
      grantValues: modalStore.selectedPermissions,
      role: roleInput.value
    };

    try {
      const response = await userPermissionsManager.inviteChildUser(request);

      const status = response.data?.status ?? "";

      if (!status.includes("Linked")) {
        errorModal(status, "Add User Unsuccessful");
        return;
      }

      successModal("An email has been sent to the new user to create their login credentials.", "New user added!");
    } catch (error) {
      logApiError(error);

      errorModal(getErrorMessage(error), "Add User Unsuccessful");
    }
  }

  const _approveUser = async () => {
    if (!user) {
      return;
    }

    pendingModal("Approving user...");

    const request: ApproveUserRequest = {
      firstName: firstNameInput.value,
      lastName: lastNameInput.value,
      childCiamId: user.ciam,
      grantValues: modalStore.selectedPermissions,
      role: roleInput.value
    }

    try {
      await userPermissionsManager.approveChildUser(request);

      successModal("User has been approved.");
    } catch (error) {
      logApiError(error);

      errorModal(getErrorMessage(error), "An error occurred approving user");
    }
  }

  const _updateUser = async () => {
    if (!user) {
      return;
    }

    pendingModal("Updating user permissions");

    try {
      await userPermissionsManager.updateChildUserAgreements(user, modalStore.selectedPermissions);

      signalRHelper.notifyPermissionUpdate(user.ciam);

      successModal("User permissions have been updated.");
    }
    catch (error) {
      logApiError(error);

      errorModal(getErrorMessage(error), "An error occurred updating user");
    }
  }

  const inviteUpdateOrApproveUser = async (e: any) => {
    e.preventDefault();

    if (!formIsValid) {
      console.log("form not valid yet");
      return
    }

    if (isNewInvite()) {
      await _inviteUser();
      return;
    }

    if (isUpdate) {
      await _updateUser();
      return;
    }

    await _approveUser();
  }
  //#endregion

  //#region Internal Components
  const UserDetails = () => {
    const useTestData = false;
    const defaultValue = useTestData ? "Test Data" : <span dangerouslySetInnerHTML={{ __html: "&nbsp;" }} />;

    const getValue = (updateValue: string | undefined, newValue: string) => {
      const value = modalStore.isUpdate ? updateValue : newValue;

      return isNil(value) ? defaultValue : value;
    }

    const firstName = getValue(user?.firstName, firstNameInput.value);
    const lastName = getValue(user?.lastName, lastNameInput.value);
    const role = getValue(user?.role, roleInput.value);
    const mobile = getValue(user?.mobileNumber, phoneInput.value);
    const email = getValue(user?.email, emailInput.value);

    return <>
      <CloseButton />

      <h3>{firstName} {lastName}</h3>

      <h4>{role}</h4>

      <p>
        {mobile}
      </p>

      <p>
        {email}
      </p>
    </>
  }

  const CloseButton = (e: any) => {
    return (
      <button type="button" className="close">
        <i className="icon icon-close" onClick={(e) => {
          e.preventDefault();
          onClose();
        }}></i>
      </button>);
  }

  const BackButton = () => {
    if (isUpdate) {
      return null;
    }

    const back = (e: any) => {
      e.preventDefault();

      if (isPageOne) {
        onClose();
        return;
      }

      modalStore.updatePageNumber(1);
    }

    return <Button text="Back" colour="outline-black" onClick={back} />;
  }

  const DeleteButton = () => {
    if (!user) {
      return null;
    }

    return <Button text="Delete" colour="outline-black" onClick={showDeleteConfirmation} />;
  }

  const isConfirmButtonEnabled = modalStore.selectedPermissions.length > 0 || isPageOne;

  const ConfirmButton = () => {
    return <button
      type="button"
      className="btn btn-black"
      onClick={inviteUpdateOrApproveUser}
      disabled={!isConfirmButtonEnabled}>
      Confirm
    </button>
  }

  const cssClassName = "new-user" + (isPageOne ? "" : " permissions");

  const colCss = (hasError: boolean) => "col" + (hasError ? " has-error" : "");

  const formInput = (input: any) => {
    return (<div className={colCss(input.hasError)}>
      <input
        type="text"
        placeholder={input.name}
        id={input.id}
        name={input.id}
        onChange={input.valueChangeHandler}
        onBlur={input.inputBlurHandler}
        defaultValue={input.value}
      />

      <label htmlFor={input.id}>
        {input.name}
      </label>

      <ValidationMessage
        valid={!input.hasError}
        message={input.errorMessage}
        showBlankMessage={true}
      />
    </div>);
  }
  //#endregion

  return (
    <ModalDialog store={modalStore}>
      <div className="floaty-form">
        <form
          id={id}
          className={cssClassName}
        >
          {isPageOne && <>
            <div className="header-row">
              <h2>User Details</h2>

              <CloseButton />
            </div>

            <section className="user-details">

              <div className="row two-cols">
                {formInput(firstNameInput)}

                {formInput(lastNameInput)}
              </div>

              {
                isNewInvite() &&
                <div className="row two-cols">
                  {formInput(phoneInput)}

                  {formInput(emailInput)}
                </div>
              }

              <div className="row two-cols">
                {formInput(roleInput)}

                <div className="col"></div>
              </div>
            </section>
          </>}

          {showPermissions && <>
            <section className="user-info">
              <UserDetails />

              <div className="buttons">
                <DeleteButton />

                {isPageOne && <BackButton />}
              </div>
            </section>

            <section className="permissions">
              <h3>Assign User Permissions</h3>

              <CloseButton />

              <UserPermissionCheckboxes
                handleAllCheckboxChange={handleAllCheckboxChange}
                handlePermissionChange={handlePermissionChange}
                modalStore={modalStore} />

              <ValidationMessage
                valid={isConfirmButtonEnabled}
                message="Please select at least one permission to continue"
                showBlankMessage={true}
              />

              <ConfirmButton />
            </section>
          </>}

          <div className="row modal-buttons-row">
            {!isPageOne && <>
              <BackButton />

              <ConfirmButton />
            </>}

            {isPageOne && <>
              <DeleteButton />

              <BackButton />

              <Button
                text="Continue"
                className="btn-black"
                onClick={nextPage}
                disabled={!areInputsAllValid()} />
            </>}
          </div>
        </form>
      </div>
    </ModalDialog>
  );
});

export default NewUserForm;
